export type OsNotificationKind = "info" | "success" | "warning" | "error";
export type OsNotification = {
  id: string; eventKey: string; title: string; message: string; kind: OsNotificationKind;
  createdAt: number; read: boolean; dismissed?: boolean; appPath?: string;
};
export type NotificationInput = Omit<OsNotification, "id" | "eventKey" | "createdAt" | "read" | "dismissed"> & { eventKey?: string };
type Pending = { key: string; action: "publish"; item: OsNotification } | { key: string; action: "read" | "dismiss"; eventKeys: string[] };
type Inbox = { items: OsNotification[]; pending: Pending[] };
export type NotificationRemote = {
  list: () => Promise<OsNotification[]>; publish: (item: OsNotification) => Promise<void>;
  acknowledge: (eventKeys: string[], dismiss: boolean) => Promise<void>;
};
export const osNotificationEvent = "base-sul-os:notification";
export const osNotificationsChangedEvent = "base-sul-os:notifications-changed";
export type NotificationSyncStatus = "local" | "syncing" | "synced" | "offline";
let account = "anonymous";
let generation = 0;
let status: NotificationSyncStatus = "local";
let requestSync: (() => void) | null = null;
const maximumItems = 100;
const routes = new Set(["/codex", "/central-projetos", "/operacoes", "/status", "/navegador", "/projetos", "/arquivos", "/"]);
const keyPattern = /^[a-zA-Z0-9:_./-]{1,200}$/;
const storageKey = () => `base-sul-os:notifications:v2:${account}`;
export function notificationSyncStatus() { return status; }
export function loadNotifications(): OsNotification[] { return readInbox().items.filter((item) => !item.dismissed).slice(0, maximumItems); }
export function pushNotification(notification: NotificationInput): OsNotification {
  const id = crypto.randomUUID();
  const item: OsNotification = { ...notification, id, eventKey: notification.eventKey && keyPattern.test(notification.eventKey) ? notification.eventKey : id,
    title: notification.title.slice(0, 160), message: notification.message.slice(0, 1000), createdAt: Date.now(), read: false,
    appPath: notification.appPath && routes.has(notification.appPath) ? notification.appPath : undefined };
  const inbox = readInbox();
  const duplicate = inbox.items.find((row) => row.eventKey === item.eventKey);
  if (duplicate) return duplicate;
  inbox.items = [item, ...inbox.items].slice(0, maximumItems);
  inbox.pending.push({ key: crypto.randomUUID(), action: "publish", item });
  save(inbox); requestSync?.();
  window.dispatchEvent(new CustomEvent(osNotificationEvent, { detail: item }));
  return item;
}
export function markAllNotificationsRead(): void { acknowledge(false); }
export function markNotificationRead(eventKey: string): void { acknowledge(false, eventKey); }
export function clearNotifications(): void { acknowledge(true); }
function acknowledge(dismiss: boolean, selectedKey?: string) {
  const inbox = readInbox();
  const eventKeys = inbox.items.filter((item) => !item.dismissed && (!selectedKey || item.eventKey === selectedKey)).map((item) => item.eventKey);
  if (!eventKeys.length) return;
  inbox.items = inbox.items.map((item) => eventKeys.includes(item.eventKey) ? { ...item, read: true, dismissed: dismiss || item.dismissed } : item);
  inbox.pending.push({ key: crypto.randomUUID(), action: dismiss ? "dismiss" : "read", eventKeys });
  save(inbox); requestSync?.();
}
/** Account-scoped cache. Legacy unscoped notices are never attributed to a user. */
export function startNotificationSync(ownerId: string, remote: NotificationRemote | null): () => void {
  const current = ++generation;
  account = ownerId || "anonymous";
  status = remote ? "syncing" : "local"; changed();
  let running = false;
  let rerun = false;
  const valid = () => generation === current;
  const sync = async () => {
    if (!remote || !valid()) return;
    if (running) { rerun = true; return; }
    running = true; status = "syncing"; changed();
    try {
      const pending = readInbox().pending;
      for (const operation of pending) {
        if (!valid()) return;
        if (operation.action === "publish") await remote.publish(operation.item);
        else await remote.acknowledge(operation.eventKeys, operation.action === "dismiss");
        if (!valid()) return;
        const inbox = readInbox();
        inbox.pending = inbox.pending.filter((item) => item.key !== operation.key); save(inbox);
      }
      const items = await remote.list();
      if (!valid()) return;
      const inbox = readInbox();
      const combined = new Map(items.filter(isNotification).map((item) => [item.eventKey, item]));
      for (const operation of inbox.pending) {
        if (operation.action === "publish" && !combined.has(operation.item.eventKey)) combined.set(operation.item.eventKey, operation.item);
        else if (operation.action !== "publish") for (const key of operation.eventKeys) {
          const item = combined.get(key);
          if (item) combined.set(key, { ...item, read: true, dismissed: operation.action === "dismiss" || item.dismissed });
        }
      }
      inbox.items = [...combined.values()].sort((a, b) => b.createdAt - a.createdAt).slice(0, maximumItems);
      status = "synced"; save(inbox);
    } catch { if (valid()) { status = "offline"; changed(); } }
    finally { running = false; if (rerun && valid()) { rerun = false; void sync(); } }
  };
  requestSync = () => { void sync(); };
  const wake = () => { if (document.visibilityState !== "hidden") void sync(); };
  window.addEventListener("online", wake); document.addEventListener("visibilitychange", wake);
  const timer = window.setInterval(wake, 30_000); void sync();
  return () => {
    window.clearInterval(timer); window.removeEventListener("online", wake); document.removeEventListener("visibilitychange", wake);
    if (valid()) { generation++; account = "anonymous"; requestSync = null; status = "local"; changed(); }
  };
}
function readInbox(): Inbox {
  try {
    const parsed = JSON.parse(localStorage.getItem(storageKey()) ?? "{}");
    return { items: Array.isArray(parsed.items) ? parsed.items.filter(isNotification).slice(0, maximumItems) : [],
      pending: Array.isArray(parsed.pending) ? parsed.pending.filter(isPending) : [] };
  } catch { return { items: [], pending: [] }; }
}
function save(inbox: Inbox) {
  try { localStorage.setItem(storageKey(), JSON.stringify(inbox)); } catch { status = "offline"; }
  changed();
}
function changed() { window.dispatchEvent(new Event(osNotificationsChangedEvent)); }
function isPending(value: unknown): value is Pending {
  const row = value as Partial<Pending> | null;
  return Boolean(row && typeof row.key === "string" && (row.action === "publish" ? isNotification(row.item)
    : ["read", "dismiss"].includes(String(row.action)) && "eventKeys" in row && Array.isArray(row.eventKeys) && row.eventKeys.length <= 100 && row.eventKeys.every((key) => typeof key === "string" && keyPattern.test(key))));
}
export function isNotification(value: unknown): value is OsNotification {
  const row = value as Partial<OsNotification> | null;
  return Boolean(row && typeof row.id === "string" && typeof row.eventKey === "string" && keyPattern.test(row.eventKey)
    && typeof row.title === "string" && row.title.length <= 160 && typeof row.message === "string" && row.message.length <= 1000
    && ["info", "success", "warning", "error"].includes(String(row.kind)) && typeof row.createdAt === "number" && Number.isFinite(row.createdAt)
    && typeof row.read === "boolean" && (row.dismissed === undefined || typeof row.dismissed === "boolean")
    && (row.appPath === undefined || routes.has(row.appPath)));
}
